import React, { createContext, useContext, useReducer, ReactNode } from 'react';

// Theme types
interface ThemeState {
  isDarkMode: boolean;
  primaryColor: string;
  accentColor: string;
}

interface ThemeContextType {
  state: ThemeState;
  toggleDarkMode: () => void;
  setPrimaryColor: (color: string) => void;
  setAccentColor: (color: string) => void;
}

// Actions
type ThemeAction =
  | { type: 'TOGGLE_DARK_MODE' }
  | { type: 'SET_PRIMARY_COLOR'; payload: string }
  | { type: 'SET_ACCENT_COLOR'; payload: string };

// Initial state
const initialState: ThemeState = {
  isDarkMode: false,
  primaryColor: '#f59e0b', // Primary golden color
  accentColor: '#ec6d1f', // Accent orange color
};

// Reducer
const themeReducer = (state: ThemeState, action: ThemeAction): ThemeState => {
  switch (action.type) {
    case 'TOGGLE_DARK_MODE':
      return { ...state, isDarkMode: !state.isDarkMode };
    case 'SET_PRIMARY_COLOR':
      return { ...state, primaryColor: action.payload };
    case 'SET_ACCENT_COLOR':
      return { ...state, accentColor: action.payload };
    default:
      return state;
  }
};

// Context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Provider
interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(themeReducer, initialState);

  const toggleDarkMode = () => {
    dispatch({ type: 'TOGGLE_DARK_MODE' });
  };

  const setPrimaryColor = (color: string) => {
    dispatch({ type: 'SET_PRIMARY_COLOR', payload: color });
  };

  const setAccentColor = (color: string) => {
    dispatch({ type: 'SET_ACCENT_COLOR', payload: color });
  };

  const value = {
    state,
    toggleDarkMode,
    setPrimaryColor,
    setAccentColor,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Hook
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
